/**
 * Program referral (affiliate growth) — separate from TherapistReferral and UserInvitation.
 * Uses generateClient when models exist; falls back to direct GraphQL when local amplify_outputs is stale.
 */
import { client } from './amplifyClient';
import { getMiraPlusAppUrl, getTinyWinsMainAppUrl, isMiraPlusHost } from './miraplusAppUrl';
import { getParentInviteOrigin } from './parentInviteOrigin';
import {
  createProgramReferralCodeGraphql,
  createProgramReferralSignupGraphql,
  formatProgramReferralApiError,
  listProgramReferralCodesGraphql,
  listProgramReferralSignupsGraphql,
} from './programReferralGraphql';

export const PENDING_PROGRAM_REFERRAL_KEY = 'pendingProgramReferralCode';

export type ProgramReferralOwnerRole = 'PARENT' | 'THERAPIST' | 'ORG_ADMIN';
export type ProductSurface = 'TINYWINS' | 'MIRAPLUS';

export type ProgramReferralCodeRow = {
  id: string;
  ownerUserId: string;
  ownerRole: ProgramReferralOwnerRole;
  code: string;
  organizationId?: string | null;
  active: boolean;
  createdAt: string;
};

export type ProgramReferralSignupRow = {
  id: string;
  codeId: string;
  code: string;
  referrerUserId: string;
  referredUserId: string;
  referredRole?: string | null;
  referredEmail?: string | null;
  referredName?: string | null;
  productSurface?: ProductSurface | null;
  registeredAt: string;
};

type ModelsAny = Record<string, any>;

function models(): ModelsAny | null {
  const m = (client as { models?: ModelsAny }).models;
  return m?.ProgramReferralCode && m?.ProgramReferralSignup ? m : null;
}

export function normalizeProgramReferralCode(raw: string): string {
  return String(raw || '')
    .trim()
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, '')
    .slice(0, 12);
}

export function captureProgramReferralFromSearch(search: string | URLSearchParams): void {
  try {
    const params = typeof search === 'string' ? new URLSearchParams(search) : search;
    const raw = params.get('ref') || params.get('programRef');
    const code = normalizeProgramReferralCode(raw || '');
    if (code.length >= 4) {
      localStorage.setItem(PENDING_PROGRAM_REFERRAL_KEY, code);
    }
  } catch {
    // ignore
  }
}

export function peekPendingProgramReferralCode(): string | null {
  try {
    const code = normalizeProgramReferralCode(localStorage.getItem(PENDING_PROGRAM_REFERRAL_KEY) || '');
    return code.length >= 4 ? code : null;
  } catch {
    return null;
  }
}

export function clearPendingProgramReferralCode(): void {
  try {
    localStorage.removeItem(PENDING_PROGRAM_REFERRAL_KEY);
  } catch {
    // ignore
  }
}

function randomCodePart(len = 6): string {
  const alphabet = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
  let out = '';
  const bytes = new Uint8Array(len);
  if (typeof crypto !== 'undefined' && crypto.getRandomValues) {
    crypto.getRandomValues(bytes);
  } else {
    for (let i = 0; i < len; i++) bytes[i] = Math.floor(Math.random() * 256);
  }
  for (let i = 0; i < len; i++) out += alphabet[bytes[i]! % alphabet.length];
  return out;
}

function mapCode(row: any): ProgramReferralCodeRow {
  return {
    id: String(row.id),
    ownerUserId: String(row.ownerUserId),
    ownerRole: String(row.ownerRole || 'PARENT').toUpperCase() as ProgramReferralOwnerRole,
    code: String(row.code || '').toUpperCase(),
    organizationId: row.organizationId ?? null,
    active: row.active !== false,
    createdAt: String(row.createdAt || new Date().toISOString()),
  };
}

function mapSignup(row: any): ProgramReferralSignupRow {
  return {
    id: String(row.id),
    codeId: String(row.codeId),
    code: String(row.code || '').toUpperCase(),
    referrerUserId: String(row.referrerUserId),
    referredUserId: String(row.referredUserId),
    referredRole: row.referredRole ?? null,
    referredEmail: row.referredEmail ?? null,
    referredName: row.referredName ?? null,
    productSurface: (row.productSurface as ProductSurface) ?? null,
    registeredAt: String(row.registeredAt || new Date().toISOString()),
  };
}

export function resolveProgramReferralOwnerRole(role: string | null | undefined): ProgramReferralOwnerRole {
  const r = String(role || '').toUpperCase();
  if (r === 'ORG_ADMIN') return 'ORG_ADMIN';
  if (r === 'THERAPIST' || r === 'CLINICIAN' || r === 'INTERNS' || r === 'CAREGIVER') return 'THERAPIST';
  return 'PARENT';
}

/** Parent-facing share links always go to TinyWins register. */
export function buildParentProgramReferralUrl(code: string): string {
  const origin = getParentInviteOrigin().replace(/\/+$/, '');
  const url = new URL('/register', `${origin}/`);
  url.searchParams.set('ref', normalizeProgramReferralCode(code));
  return url.toString();
}

/**
 * Therapist / clinic share links use the product surface where the link is generated.
 * TinyWins org dashboard → tinywins.ai; MiraPlus → miraplus.ai (shared backend, different frontends).
 */
export function buildTherapistProgramReferralUrl(code: string): string {
  let origin: string;
  if (typeof window !== 'undefined' && window.location?.origin) {
    origin = window.location.origin;
  } else if (isMiraPlusHost()) {
    origin = getMiraPlusAppUrl();
  } else {
    origin = getTinyWinsMainAppUrl();
  }
  origin = origin.replace(/\/+$/, '');
  const url = new URL('/register', `${origin}/`);
  url.searchParams.set('ref', normalizeProgramReferralCode(code));
  return url.toString();
}

export function buildProgramReferralShareUrl(code: string, ownerRole: ProgramReferralOwnerRole): string {
  if (ownerRole === 'PARENT') return buildParentProgramReferralUrl(code);
  return buildTherapistProgramReferralUrl(code);
}

export function currentProductSurface(): ProductSurface {
  return isMiraPlusHost() ? 'MIRAPLUS' : 'TINYWINS';
}

async function listCodesByOwner(ownerUserId: string): Promise<ProgramReferralCodeRow[]> {
  const m = models();
  if (m) {
    const resp = await m.ProgramReferralCode.list({
      filter: { ownerUserId: { eq: ownerUserId } },
      authMode: 'apiKey',
    });
    return (resp.data || []).filter(Boolean).map(mapCode);
  }
  const rows = await listProgramReferralCodesGraphql({ ownerUserId: { eq: ownerUserId } });
  return rows.map(mapCode);
}

export async function listProgramReferralCodesForOwner(ownerUserId: string): Promise<ProgramReferralCodeRow[]> {
  if (!ownerUserId) return [];
  try {
    return await listCodesByOwner(ownerUserId);
  } catch (err) {
    console.warn('listProgramReferralCodesForOwner', err);
    return [];
  }
}

export async function getOrCreateProgramReferralCode(params: {
  ownerUserId: string;
  ownerRole: ProgramReferralOwnerRole;
  organizationId?: string | null;
}): Promise<ProgramReferralCodeRow> {
  try {
    const existing = await listCodesByOwner(params.ownerUserId);
    const active = existing.find((c) => c.active);
    if (active) return active;

    const prefix =
      params.ownerRole === 'ORG_ADMIN' ? 'ORG' : params.ownerRole === 'THERAPIST' ? 'TH' : 'PA';
    let code = `${prefix}${randomCodePart(6)}`;
    for (let attempt = 0; attempt < 5; attempt++) {
      const clash = models()
        ? (
            await models()!.ProgramReferralCode.list({
              filter: { code: { eq: code } },
              authMode: 'apiKey',
            })
          ).data || []
        : await listProgramReferralCodesGraphql({ code: { eq: code } }, 5);
      if (!clash.length) break;
      code = `${prefix}${randomCodePart(6)}`;
    }

    const input = {
      ownerUserId: params.ownerUserId,
      ownerRole: params.ownerRole,
      code,
      organizationId: params.organizationId || undefined,
      active: true,
      createdAt: new Date().toISOString(),
    };

    if (models()) {
      const created = await models()!.ProgramReferralCode.create(input, { authMode: 'apiKey' });
      if (created.errors?.length || !created.data) {
        throw new Error(created.errors?.[0]?.message || 'Failed to create referral code');
      }
      return mapCode(created.data);
    }

    return mapCode(await createProgramReferralCodeGraphql(input));
  } catch (err) {
    throw new Error(formatProgramReferralApiError(err));
  }
}

export async function listSignupsForReferrer(referrerUserId: string): Promise<ProgramReferralSignupRow[]> {
  if (!referrerUserId) return [];
  try {
    if (models()) {
      const resp = await models()!.ProgramReferralSignup.list({
        filter: { referrerUserId: { eq: referrerUserId } },
        authMode: 'apiKey',
      });
      return (resp.data || [])
        .filter(Boolean)
        .map(mapSignup)
        .sort((a, b) => String(b.registeredAt).localeCompare(String(a.registeredAt)));
    }
    const rows = await listProgramReferralSignupsGraphql({ referrerUserId: { eq: referrerUserId } });
    return rows.map(mapSignup).sort((a, b) => String(b.registeredAt).localeCompare(String(a.registeredAt)));
  } catch (err) {
    console.warn('listSignupsForReferrer', err);
    return [];
  }
}

export async function listAllProgramReferralCodes(limit = 200): Promise<ProgramReferralCodeRow[]> {
  try {
    if (models()) {
      const resp = await models()!.ProgramReferralCode.list({ limit, authMode: 'apiKey' });
      return (resp.data || []).filter(Boolean).map(mapCode);
    }
    return (await listProgramReferralCodesGraphql(undefined, limit)).map(mapCode);
  } catch (err) {
    console.warn('listAllProgramReferralCodes', err);
    return [];
  }
}

export async function listAllProgramReferralSignups(limit = 500): Promise<ProgramReferralSignupRow[]> {
  try {
    if (models()) {
      const resp = await models()!.ProgramReferralSignup.list({ limit, authMode: 'apiKey' });
      return (resp.data || [])
        .filter(Boolean)
        .map(mapSignup)
        .sort((a, b) => String(b.registeredAt).localeCompare(String(a.registeredAt)));
    }
    return (await listProgramReferralSignupsGraphql(undefined, limit))
      .map(mapSignup)
      .sort((a, b) => String(b.registeredAt).localeCompare(String(a.registeredAt)));
  } catch (err) {
    console.warn('listAllProgramReferralSignups', err);
    return [];
  }
}

async function findActiveCodeBySlug(code: string): Promise<ProgramReferralCodeRow | null> {
  const normalized = normalizeProgramReferralCode(code);
  if (!normalized) return null;
  if (models()) {
    const resp = await models()!.ProgramReferralCode.list({
      filter: { code: { eq: normalized } },
      authMode: 'apiKey',
    });
    const row = (resp.data || []).find((c: any) => c && c.active !== false);
    return row ? mapCode(row) : null;
  }
  const rows = await listProgramReferralCodesGraphql({ code: { eq: normalized } }, 10);
  const row = rows.find((c) => c.active !== false);
  return row ? mapCode(row) : null;
}

/**
 * Idempotent: one ProgramReferralSignup per referredUserId.
 * Call after the referred user's app User row exists.
 */
export async function attributeProgramReferralSignup(params: {
  referredUserId: string;
  referredRole?: string | null;
  referredEmail?: string | null;
  referredName?: string | null;
  productSurface?: ProductSurface;
}): Promise<ProgramReferralSignupRow | null> {
  const pending = peekPendingProgramReferralCode();
  if (!pending || !params.referredUserId) return null;

  try {
    let already: any[] = [];
    if (models()) {
      const resp = await models()!.ProgramReferralSignup.list({
        filter: { referredUserId: { eq: params.referredUserId } },
        authMode: 'apiKey',
      });
      already = resp.data || [];
    } else {
      already = await listProgramReferralSignupsGraphql({ referredUserId: { eq: params.referredUserId } }, 5);
    }
    if (already.length) {
      clearPendingProgramReferralCode();
      return mapSignup(already[0]);
    }

    const codeRow = await findActiveCodeBySlug(pending);
    if (!codeRow) {
      clearPendingProgramReferralCode();
      return null;
    }

    if (codeRow.ownerUserId === params.referredUserId) {
      clearPendingProgramReferralCode();
      return null;
    }

    const input = {
      codeId: codeRow.id,
      code: codeRow.code,
      referrerUserId: codeRow.ownerUserId,
      referredUserId: params.referredUserId,
      referredRole: params.referredRole || undefined,
      referredEmail: params.referredEmail || undefined,
      referredName: params.referredName || undefined,
      productSurface: params.productSurface || currentProductSurface(),
      registeredAt: new Date().toISOString(),
    };

    if (models()) {
      const created = await models()!.ProgramReferralSignup.create(input, { authMode: 'apiKey' });
      clearPendingProgramReferralCode();
      if (!created.data) return null;
      return mapSignup(created.data);
    }

    const created = await createProgramReferralSignupGraphql(input);
    clearPendingProgramReferralCode();
    return mapSignup(created);
  } catch (err) {
    console.warn('attributeProgramReferralSignup', err);
    return null;
  }
}

export function formatSignupDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

/** TinyWins parent app origin helper for share copy (unused hosts). */
export function tinyWinsOriginForCopy(): string {
  return getTinyWinsMainAppUrl();
}
